import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Ticket, Trophy } from "lucide-react";

export default function Account() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/entries").then((r) => setEntries(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const grouped = entries.reduce((acc, e) => {
    const k = e.competition_id;
    acc[k] = acc[k] || { competition: e.competition, tickets: [] };
    acc[k].tickets.push(e.ticket_number);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-20" data-testid="account-page">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Account</div>
        <h1 className="font-display text-4xl md:text-6xl text-white mt-2">My entries</h1>
        <p className="text-white/60 mt-2">Logged in as <span className="text-white font-bold">{user?.email}</span></p>
      </div>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="ufr-card rounded-md p-10 text-center" data-testid="no-entries-card">
          <Trophy size={32} className="text-[color:var(--ufr-green)] mx-auto" />
          <div className="font-display text-2xl text-white mt-4">No entries yet</div>
          <p className="text-white/60 text-sm mt-2">Enter a live raffle to start collecting tickets.</p>
          <Link to="/competitions" className="btn-ufr-primary inline-block px-6 py-3 text-sm mt-6" data-testid="account-browse-btn">Browse raffles</Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="entries-grid">
          {Object.entries(grouped).map(([cid, { competition, tickets }]) => (
            <div key={cid} className="ufr-card rounded-md overflow-hidden" data-testid={`entry-card-${cid}`}>
              <div className="relative aspect-[4/3] bg-black">
                {competition?.image_url && (
                  <img src={competition.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">{competition?.subtitle}</div>
                  <div className="font-display text-lg text-white">{competition?.title}</div>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold">Your tickets</div>
                  <div className="ufr-chip"><Ticket size={12} /> {tickets.length}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tickets.slice(0, 20).map((n) => (
                    <span key={n} className="text-[11px] font-bold bg-black/60 border border-[color:var(--ufr-green)]/30 text-[color:var(--ufr-green)] px-2 py-0.5 rounded-sm">#{n}</span>
                  ))}
                  {tickets.length > 20 && (
                    <span className="text-[11px] text-white/50">+{tickets.length - 20} more</span>
                  )}
                </div>
                <Link to={`/competitions/${cid}`} className="block text-center btn-ufr-ghost py-2 text-xs mt-4">View raffle</Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
